import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, RefreshCw, Loader2, Star, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function GoogleReviewsPanel() {
  const { data: places = [], refetch } = useQuery({ queryKey: ["admin-google-places"], queryFn: () => api.get("/admin/google-places").then((r) => r.data) });
  const { data: preview, refetch: refetchPreview } = useQuery({ queryKey: ["admin-google-preview"], queryFn: () => api.get("/google-reviews").then((r) => r.data) });
  const [form, setForm] = useState({ place_id: "", label: "", city: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.place_id.trim()) return toast.error("Enter a Google Place ID");
    setBusy(true);
    try {
      await api.post("/admin/google-places", form);
      toast.success("Location added — reviews will appear shortly");
      setForm({ place_id: "", label: "", city: "" }); refetch(); refetchPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Remove ${p.label || p.place_id}?`)) return;
    try { await api.delete(`/admin/google-places/${p.id}`); toast.success("Removed"); refetch(); refetchPreview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refresh = async () => {
    setBusy(true);
    try { await api.post("/admin/google-reviews/refresh"); toast.success("Reviews refreshed"); refetchPreview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-google-reviews-panel">
      <div className="flex items-center justify-between mb-2">
        <div><h2 className="font-serif text-2xl text-[#1A242B]">Google Reviews</h2><p className="text-sm text-[#4A5D6B]">Add each location's Google Place ID to surface its reviews on the homepage and Testimonials page. Any center can be added here.</p></div>
        <button onClick={refresh} disabled={busy} data-testid="google-refresh-btn" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Refresh</button>
      </div>

      <div className="eyw-card !p-5 my-5">
        <p className="text-xs text-[#8FA89B] mb-2">Find a Place ID at developers.google.com/maps/documentation/places/web-service/place-id</p>
        <div className="grid sm:grid-cols-[1.4fr_1fr_1fr_auto] gap-2 items-end">
          <div><label className="text-xs text-[#4A5D6B]">Place ID</label><input value={form.place_id} onChange={(e) => setForm({ ...form, place_id: e.target.value })} placeholder="ChIJ…" data-testid="google-place-id" className="eyw-input w-full text-sm" /></div>
          <div><label className="text-xs text-[#4A5D6B]">Label</label><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Center name" data-testid="google-place-label" className="eyw-input w-full text-sm" /></div>
          <div><label className="text-xs text-[#4A5D6B]">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City, ST" className="eyw-input w-full text-sm" /></div>
          <button onClick={add} disabled={busy} data-testid="google-add-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60"><Plus size={15} /> Add</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {places.map((p) => {
          const pv = preview?.places?.find((x) => x.place_id === p.place_id);
          return (
            <div key={p.id} className="eyw-card !p-5 flex items-start justify-between gap-3" data-testid={`google-place-${p.place_id}`}>
              <div>
                <h3 className="font-medium text-[#1A242B]">{p.label || p.place_id}</h3>
                {p.city && <p className="text-xs text-[#8FA89B] flex items-center gap-1"><MapPin size={11} /> {p.city}</p>}
                {pv && <p className="text-sm text-[#4A5D6B] mt-1 flex items-center gap-1.5"><Star size={13} className="fill-[#E8B04B] text-[#E8B04B]" /> {pv.rating || "—"} · {pv.user_rating_count || 0} reviews · {(pv.reviews || []).length} shown</p>}
                <p className="text-[10px] text-[#B7B0A4] mt-1 font-mono truncate max-w-[220px]">{p.place_id}</p>
              </div>
              <button onClick={() => del(p)} className="text-red-500 shrink-0"><Trash2 size={16} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
